import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Dashboard/Header";
import { tokens } from "../../../theme";
import axios from "axios";
import { schemeCategory10 } from 'd3-scale-chromatic';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Shared TextField styles
const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.9rem",
    height: "45px",
    "& fieldset": { borderColor: "#771726" },
    "&:hover fieldset": { borderColor: "#771726" },
    "&.Mui-focused fieldset": { borderColor: "#771726" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    color: "#000000",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
};

// Current datetime-helper
const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtNoSeconds = (v) =>
  typeof v === "string" ? v.replace(/:\d{2}$/, "") : v;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1, bgcolor: '#222', border: '1px solid #555' }}>
      <Typography variant="caption" sx={{ color: '#ddd' }}>
        {fmtNoSeconds(label)}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name || entry.dataKey}: {entry.value}
        </Typography>
      ))}
    </Paper>
  );
}



// SERIES (para checkboxes/leyenda/gráfico)
const seriesOptions = [
  // Analógicas
  { key: "tempHeladaIn",             label: "Temp. Helada In" },
  { key: "tempHeladaOut",            label: "Temp. Helada Out" },
  { key: "tempCondensacionIn",       label: "Temp. Condensación In" },
  { key: "tempCondensacionOut",      label: "Temp. Condensación Out" },
  { key: "tempCondensacionSecIn",    label: "Temp. Condensación Secundaria In" },
  { key: "tempCondensacionSecOut",   label: "Temp. Condensación Secundaria Out" },

  { key: "presSuccion1",             label: "Pres. Succión 1" },
  { key: "presSuccion2",             label: "Pres. Succión 2" },
  { key: "presSuccion3",             label: "Pres. Succión 3" },
  { key: "presSuccion4",             label: "Pres. Succión 4" },

  { key: "presDescarga1",            label: "Pres. Descarga 1" },
  { key: "presDescarga2",            label: "Pres. Descarga 2" },
  { key: "presDescarga3",            label: "Pres. Descarga 3" },
  { key: "presDescarga4",            label: "Pres. Descarga 4" },

  { key: "presDiferencialAceite1",   label: "Pres. Diferencial de Aceite 1" },
  { key: "presDiferencialAceite2",   label: "Pres. Diferencial de Aceite 2" },
  { key: "presDiferencialAceite3",   label: "Pres. Diferencial de Aceite 3" },
  { key: "presDiferencialAceite4",   label: "Pres. Diferencial de Aceite 4" },

  // Digitales
  { key: "habilitaCompresor1",       label: "Habilita Compresor 1" },
  { key: "habilitaCompresor2",       label: "Habilita Compresor 2" },
  { key: "habilitaCompresor3",       label: "Habilita Compresor 3" },
  { key: "habilitaCompresor4",       label: "Habilita Compresor 4" },

  { key: "estatusCompresor1",        label: "Estatus Compresor 1" },
  { key: "estatusCompresor2",        label: "Estatus Compresor 2" },
  { key: "estatusCompresor3",        label: "Estatus Compresor 3" },
  { key: "estatusCompresor4",        label: "Estatus Compresor 4" },

  { key: "flujoAguaHelada",          label: "Flujo Agua Helada" },
  { key: "flujoAguaCondensador",     label: "Flujo Agua Condensador" },
  { key: "flujoCondensadorSecundario", label: "Flujo Condensador Secundario" },

  { key: "arranqueRemoto",           label: "Arranque Remoto" },

  { key: "encendidoCompresor1",      label: "Encendido Compresor 1" },
  { key: "encendidoCompresor2",      label: "Encendido Compresor 2" },
  { key: "encendidoCompresor3",      label: "Encendido Compresor 3" },
  { key: "encendidoCompresor4",      label: "Encendido Compresor 4" },

  { key: "bombaAlberca",             label: "Bomba Alberca" },
];

const GCoTherm4Comp2CC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [idEquipo, setIdEquipo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(nowLocal());
  const [fechaFinal, setFechaFinal] = useState(nowLocal());

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  // Data state
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const MACHINE  = "CoTherm 4Comp 2CC";
  const QUERY    = "Grafica";


  // toggle selection
  const toggleKey = (k) => {
    setSelectedKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const fetchRows = async () => {
  // 1) validación de fechas
  if (new Date(fechaFinal) < new Date(fechaInicio)) {
    setError("La fecha final no puede ser anterior a la fecha de inicio.");
    return;
  }

  setError(null);
  setLoading(true);
  setRows([]);

  try {
    // 2) petición
    const res = await axios.post(
  `${API_BASE}/test/consulta/` +
    `${encodeURIComponent(MACHINE)}/` +
    `${encodeURIComponent(QUERY)}`,
  { idEquipo, fechaInicio, fechaFinal },
  { headers: { "Content-Type": "application/json" } }
  );


    // 3) parsear body
    const body = res.data.body
      ? (typeof res.data.body === "string"
          ? JSON.parse(res.data.body)
          : res.data.body)
      : res.data;

    console.log("👉 body ya parseado:", JSON.stringify(body, null, 2));

    // 4) extraer array
    const extractedData = Array.isArray(body.data)
      ? body.data
      : (Array.isArray(body.data?.[0]) ? body.data[0] : []);

    // 5) limpiar claves
    const normalizeKeys = row => {
      const clean = {};
      for (const k in row) clean[k.trim()] = row[k];
      return clean;
    };
    
    // 6) mapeo completo (copia aquí TODOS tus campos)
    const mapped = extractedData.map((row, i) => {
      const r = normalizeKeys(row);
      const fechaUTC = new Date(r["Fecha"]);
      const ts = fechaUTC.getTime();
      const formattedDate = 
        `${fechaUTC.getUTCFullYear()}-${String(fechaUTC.getUTCMonth()+1).padStart(2,"0")}`+
        `-${String(fechaUTC.getUTCDate()).padStart(2,"0")} `+
        `${String(fechaUTC.getUTCHours()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCMinutes()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCSeconds()).padStart(2,"0")}`;

        const base = {
          id: i + 1,
          ts,
          tipoMaquinaControl: row["Tipo de Máquina y Control"],
          numeroEquipo: row["Número de Equipo"],
          fecha: formattedDate, // ✅ Se usa la fecha convertida aquí
          tempHeladaIn: row["Temp Helada In"],
          tempHeladaOut: row["Temp Helada Out"],
          tempCondensacionIn: row["Temp Condensacion In"],
          tempCondensacionOut: row["Temp Condensacion Out"],
          tempCondensacionSecIn: row["Temp Condensacion Secundaria In"],
          tempCondensacionSecOut: row["Temp Condensacion Secundaria Out"],
          presSuccion1: row["Pres Succion 1"],
          presSuccion2: row["Pres Succion 2"],
          presSuccion3: row["Pres Succion 3"],
          presSuccion4: row["Pres Succion 4"],
          presDescarga1: row["Pres Descarga 1"],
          presDescarga2: row["Pres Descarga 2"],
          presDescarga3: row["Pres Descarga 3"],
          presDescarga4: row["Pres Descarga 4"],
          presDiferencialAceite1: row["Pres Diferencial de Aceite 1"],
          presDiferencialAceite2: row["Pres Diferencial de Aceite 2"],
          presDiferencialAceite3: row["Pres Diferencial de Aceite 3"],
          presDiferencialAceite4: row["Pres Diferencial de Aceite 4"],
          habilitaCompresor1: row["Habilita Compresor 1"],
          habilitaCompresor2: row["Habilita Compresor 2"],
          habilitaCompresor3: row["Habilita Compresor 3"],
          habilitaCompresor4: row["Habilita Compresor 4"],
          estatusCompresor1: row["Estatus Compresor 1"],
          estatusCompresor2: row["Estatus Compresor 2"],
          estatusCompresor3: row["Estatus Compresor 3"],
          estatusCompresor4: row["Estatus Compresor 4"],
          flujoAguaHelada: row["Flujo Agua Helada"],
          flujoAguaCondensador: row["Flujo Agua Condensador"],
          flujoCondensadorSecundario: row["Flujo Condensador Secundario"],
          arranqueRemoto: row["Arranque Remoto"],
          encendidoCompresor1: row["Encendido Compresor 1"],
          encendidoCompresor2: row["Encendido Compresor 2"],
          encendidoCompresor3: row["Encendido Compresor 3"],
          encendidoCompresor4: row["Encendido Compresor 4"],
          bombaAlberca: row["Bomba Alberca"],
          };

    // 🔌 OPCIONAL (pero recomendable si tus datos vienen como strings/ON/OFF):
     //Normaliza todo lo que no sea texto a número/0-1
     const parseValue = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const s = String(v).trim().toLowerCase();
       if (["true","on","encendido","sí","si","1"].includes(s)) return 1;
       if (["false","off","apagado","no","0"].includes(s)) return 0;
       const n = Number(s.replace(",", "."));
       return Number.isFinite(n) ? n : v;
    };
     const TEXT_KEYS = new Set(["id","tipoMaquinaControl","numeroEquipo","fecha","ts"]);
     Object.keys(base).forEach(k => {
       if (!TEXT_KEYS.has(k)) base[k] = parseValue(base[k]);
     });

    return base;
      })

    // 6.1) ✅ Ordena una sola vez aquí
    mapped.sort((a, b) => {
      const ta = a.ts ?? Date.parse(a.fecha) ?? 0;
      const tb = b.ts ?? Date.parse(b.fecha) ?? 0;
      return ta - tb; // ascendente: del más viejo al más nuevo
    });

      setRows(mapped);

  } catch (err) {
    console.error(err);
    setError("Error al consultar los datos. Intenta de nuevo.");
  } finally {
    setLoading(false);
  }
};

  // keys que corresponden a series análogas y digitales
  const analogKeys = [
    "tempHeladaIn", "tempHeladaOut", "tempCondensacionIn", "tempCondensacionOut",
    "tempCondensacionSecIn", "tempCondensacionSecOut", "presSuccion1", "presSuccion2",
    "presSuccion3", "presSuccion4", "presDescarga1", "presDescarga2",
    "presDescarga3", "presDescarga4", "presDiferencialAceite1", "presDiferencialAceite2",
    "presDiferencialAceite3", "presDiferencialAceite4",
  ];

  const digitalKeys = [
    "habilitaCompresor1", "habilitaCompresor2", "habilitaCompresor3", "habilitaCompresor4",
    "estatusCompresor1", "estatusCompresor2", "estatusCompresor3", "estatusCompresor4",
    "flujoAguaHelada", "flujoAguaCondensador", "flujoCondensadorSecundario", "arranqueRemoto",
    "encendidoCompresor1", "encendidoCompresor2", "encendidoCompresor3", "encendidoCompresor4",
    "bombaAlberca",
  ];


  // COLUMNAS (DataGrid)
const columns = [
  { field: "id", headerName: "ID", width: 90 },

  // Meta
  { field: "tipoMaquinaControl", headerName: "Tipo de Máquina y Control", minWidth: 220, flex: 1 },
  { field: "numeroEquipo",       headerName: "Número de Equipo",          minWidth: 160, flex: 1 },
  { field: "fecha",              headerName: "Fecha",                      minWidth: 180, flex: 1 },

  // Analógicas
  { field: "tempHeladaIn",             headerName: "Temp. Helada In",                    minWidth: 190, flex: 1 },
  { field: "tempHeladaOut",            headerName: "Temp. Helada Out",                   minWidth: 190, flex: 1 },
  { field: "tempCondensacionIn",       headerName: "Temp. Condensación In",              minWidth: 210, flex: 1 },
  { field: "tempCondensacionOut",      headerName: "Temp. Condensación Out",             minWidth: 210, flex: 1 },
  { field: "tempCondensacionSecIn",    headerName: "Temp. Condensación Secundaria In",   minWidth: 250, flex: 1 },
  { field: "tempCondensacionSecOut",   headerName: "Temp. Condensación Secundaria Out",  minWidth: 250, flex: 1 },

  { field: "presSuccion1",             headerName: "Pres. Succión 1",                    minWidth: 180, flex: 1 },
  { field: "presSuccion2",             headerName: "Pres. Succión 2",                    minWidth: 180, flex: 1 },
  { field: "presSuccion3",             headerName: "Pres. Succión 3",                    minWidth: 180, flex: 1 },
  { field: "presSuccion4",             headerName: "Pres. Succión 4",                    minWidth: 180, flex: 1 },

  { field: "presDescarga1",            headerName: "Pres. Descarga 1",                   minWidth: 180, flex: 1 },
  { field: "presDescarga2",            headerName: "Pres. Descarga 2",                   minWidth: 180, flex: 1 },
  { field: "presDescarga3",            headerName: "Pres. Descarga 3",                   minWidth: 180, flex: 1 },
  { field: "presDescarga4",            headerName: "Pres. Descarga 4",                   minWidth: 180, flex: 1 },

  { field: "presDiferencialAceite1",   headerName: "Pres. Diferencial de Aceite 1",      minWidth: 230, flex: 1 },
  { field: "presDiferencialAceite2",   headerName: "Pres. Diferencial de Aceite 2",      minWidth: 230, flex: 1 },
  { field: "presDiferencialAceite3",   headerName: "Pres. Diferencial de Aceite 3",      minWidth: 230, flex: 1 },
  { field: "presDiferencialAceite4",   headerName: "Pres. Diferencial de Aceite 4",      minWidth: 230, flex: 1 },

  // Digitales
  { field: "habilitaCompresor1",       headerName: "Habilita Compresor 1",               minWidth: 200, flex: 1 },
  { field: "habilitaCompresor2",       headerName: "Habilita Compresor 2",               minWidth: 200, flex: 1 },
  { field: "habilitaCompresor3",       headerName: "Habilita Compresor 3",               minWidth: 200, flex: 1 },
  { field: "habilitaCompresor4",       headerName: "Habilita Compresor 4",               minWidth: 200, flex: 1 },

  { field: "estatusCompresor1",        headerName: "Estatus Compresor 1",                minWidth: 200, flex: 1 },
  { field: "estatusCompresor2",        headerName: "Estatus Compresor 2",                minWidth: 200, flex: 1 },
  { field: "estatusCompresor3",        headerName: "Estatus Compresor 3",                minWidth: 200, flex: 1 },
  { field: "estatusCompresor4",        headerName: "Estatus Compresor 4",                minWidth: 200, flex: 1 },

  { field: "flujoAguaHelada",          headerName: "Flujo Agua Helada",                  minWidth: 180, flex: 1 },
  { field: "flujoAguaCondensador",     headerName: "Flujo Agua Condensador",             minWidth: 210, flex: 1 },
  { field: "flujoCondensadorSecundario", headerName: "Flujo Condensador Secundario",     minWidth: 240, flex: 1 },

  { field: "arranqueRemoto",           headerName: "Arranque Remoto",                    minWidth: 190, flex: 1 },

  { field: "encendidoCompresor1",      headerName: "Encendido Compresor 1",              minWidth: 210, flex: 1 },
  { field: "encendidoCompresor2",      headerName: "Encendido Compresor 2",              minWidth: 210, flex: 1 },
  { field: "encendidoCompresor3",      headerName: "Encendido Compresor 3",              minWidth: 210, flex: 1 },
  { field: "encendidoCompresor4",      headerName: "Encendido Compresor 4",              minWidth: 210, flex: 1 },

  { field: "bombaAlberca",             headerName: "Bomba Alberca",                      minWidth: 170, flex: 1 },
];


  const dummyData = [
  { fecha: "1", value: 2 },
  { fecha: "2", value: 5.5 },
  { fecha: "3", value: 2 },
  { fecha: "5", value: 8.5 },
  { fecha: "8", value: 1.5 },
  { fecha: "10", value: 5 },
  ];

  // 1) Si tengo datos reales los uso, si no pongo un dummy
  const displayData = rows.length ? rows : dummyData;

  // 2) De mis opciones, cojo sólo las que estén marcadas
  // 3) Si no hay ninguna marcada, dibujo la serie "value" del dummy
  const seriesKeys = React.useMemo(() => {
    const displaySeriesKeys = seriesOptions
      .filter(opt => selectedKeys.includes(opt.key))
      .map(opt => opt.key);
    return displaySeriesKeys.length
      ? displaySeriesKeys
      : (rows.length ? [] : ["value"]);
  }, [selectedKeys, rows.length]);

  // 4) Genero un color para cada serie
  const lineColors = React.useMemo(
    () => seriesKeys.map((_, i) => schemeCategory10[i % schemeCategory10.length]),
    [seriesKeys]
  );

  const labelMap = React.useMemo(() => {
  const m = Object.fromEntries(seriesOptions.map(o => [o.key, o.label]));
  m.value = "Valor (demo)";
  return m;
  }, []);


  // console.log("🔥 columns que llega a DataGrid:", columns);
    
     return (

       <Box sx={{ maxWidth: 1200, mx: "auto", mt: "90px", px: 2 }}>
      <Header
        title="COTHERM 4 COMP 2CC"
        sxTitle={{ fontSize: "2rem", textAlign: "center" }}
      />

      {/* Top Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="ID Equipo"
              variant="outlined"
              fullWidth
              value={idEquipo}
              onChange={(e) => setIdEquipo(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Inicio"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Final"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaFinal}
              onChange={(e) => setFechaFinal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Middle Row: Variables con Tabs */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2, height: 400, display: "flex", flexDirection: "column" }}>
            {/* Tabs para Análogos | Digitales */}
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              <Tab label="Análogos" />
              <Tab label="Digitales" />
            </Tabs>

            {/* Panel Análogos */}
            {tabIndex === 0 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, '& .MuiFormControlLabel-root': { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter(opt => analogKeys.includes(opt.key))
                    .map(opt => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption"  sx={{ fontSize: 12, fontWeight: 'bold' }}>{opt.label}</Typography>}
                        />
                      </Grid>
                    ))
                  }
                </Grid>
              </Box>
            )}

            {/* Panel Digitales */}
            {tabIndex === 1 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, '& .MuiFormControlLabel-root': { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter(opt => digitalKeys.includes(opt.key))
                    .map(opt => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption" fontWeight="bold">{opt.label}</Typography>}
                        />
                      </Grid>
                    ))
                  }
                </Grid>
              </Box>
            )}

            {/* Botón Graficar */}
            <Button
              variant="contained"
              sx={{
                bgcolor: "#771726",
                color: "white",
                mt: 1,
                width: isMobile ? "100%" : "auto",
                "&:hover": { bgcolor: "#747274" },
              }}
              onClick={(e) => { e.preventDefault(); fetchRows(); }}
            >
              Graficar
            </Button>
          </Paper>
        </Grid>

        {/* Table placeholder */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{
              height: 400,       // la altura fija del Paper
              p: 2,
              display: "flex",   // IMPORTANTE → convierte al Paper en un contenedor flex
              flexDirection: "column"
            }}
          >

            {/*
              Aquí le cedemos todo el espacio restante al DataGrid
              envolviéndolo en un Box con flex: 1.
            */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableColumnMenu
                pageSize={10}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#771726",
                    zIndex: 1,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#771726",
                    color: "#ffffff",
                    borderTop: "none",
                  },
                  "& .MuiTablePagination-root, & .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Final Row: Full‑width Chart */}
      <Grid container mb={3}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ height: 400, p: 2, pb: 4 }}>
            {loading ? (
              <Box sx={{display:"flex",justifyContent:"center",alignItems:"center",height:"100%"}}>
                <CircularProgress/>
              </Box>
            ) : (
              <>
                <Box
                  sx={{ 
                    display: "flex", 
                    overflowX: "auto", 
                    whiteSpace: "nowrap", 
                    mb: 1,                // espacio con el chart 
                    ml: "30px",           // desplazamiento igual al margin.left del chart 
                    width: "calc(100% - 60px)", // 100% menos left + right margins (20+30) 
                  }} 
                >
                  {seriesKeys.map((key, i) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: lineColors[i],
                          mr: 1,
                        }}
                      />
                      <Typography variant="caption">{labelMap[key]}</Typography>
                    </Box>
                  ))}
                </Box>

                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    animationDuration={0}
                    data={displayData}
                    margin={{ top: 20, right: 30, left: -30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke={colors.grey[700]} />

                    <XAxis
                      dataKey="fecha"
                      interval="auto"
                      minTickGap={70}          
                      tickMargin={10}
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      tickFormatter={fmtNoSeconds} 
                    />
                    <YAxis
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      domain={["auto", "auto"]}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      delayShow={0}
                      cursor={{ stroke: colors.grey[500], strokeDasharray: "3 3" }}
                    />

                    {/* Ya no va <Legend> aquí */}

                    {seriesKeys.map((key, i) => (
                      <Line
                        key={key}
                        dataKey={key}
                        name={labelMap[key]}
                        stroke={lineColors[i]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Error Snackbar */}
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GCoTherm4Comp2CC;